/**
* TelemetryContext.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { generateW3CId, isBoolean, isNullOrUndefined, isNumber, isString, objForEachKey, onConfigChange } from "@microsoft/applicationinsights-core-js";
import { objDefineProps } from "@nevware21/ts-utils";
import { AppExtensionKeys, CloudExtKeys, DeviceExtKeys, DistributedTraceExtKeys, Extensions, IntWebExtKeys, LocExtKeys, OSExtKeys, SDKExtKeys, SessionExtKeys, TraceExtKeys, UserExtensionKeys, UtcExtKeys, WebExtensionKeys } from "./ExtensionKeys";
import { SessionManager } from "./SessionManager";
import { Application } from "./context/Application";
import { Cloud } from "./context/Cloud";
import { Device } from "./context/Device";
import { IntWeb } from "./context/IntWeb";
import { Loc } from "./context/Loc";
import { OperatingSystem } from "./context/OperatingSystem";
import { Sdk } from "./context/Sdk";
import { Session } from "./context/Session";
import { User } from "./context/User";
import { Utc } from "./context/Utc";
import { Web } from "./context/Web";
function _applyExtValues(theEvent, extension, names, map, overwriteTarget) {
    var target = theEvent.ext[Extensions[extension]];
    if (target) {
        try {
            objForEachKey(map, function (field, value) {
                if (isString(value) || isNumber(value) || isBoolean(value)) {
                    var targetValue = target[names[field]];
                    if (!overwriteTarget && (targetValue || isString(targetValue) || isNumber(targetValue) || isBoolean(targetValue))) {
                        value = targetValue;
                    }
                    target[names[field]] = value;
                }
            });
        }
        catch (e) {
            // During unload some accesses may cause a TypeError due to accessing a dead object
        }
    }
    return target;
}
function _applyValue(target, name, value, overwriteTarget) {
    if (target) {
        if (isString(value) || isNumber(value) || isBoolean(value)) {
            var targetValue = target[name];
            if (!overwriteTarget && (targetValue || isString(targetValue) || isNumber(targetValue) || isBoolean(targetValue))) {
                value = targetValue;
            }
            target[name] = value;
        }
    }
    return target;
}
function _getTraceCtx(itemCtx, defaultCore) {
    var core = defaultCore;
    var traceCtx = null;
    if (itemCtx && itemCtx.core) {
        core = itemCtx.core() || defaultCore;
    }
    if (core && core.getTraceCtx) {
        traceCtx = core.getTraceCtx() || null;
    }
    return traceCtx;
}
/**
 * Factory function that creates a TelemetryContext instance implementing IInternalTelemetryContext
 * @param coreConfig - The core configuration
 * @param propertiesConfig - The properties configuration
 * @param core - The App Insights core instance
 * @param unloadHookContainer - Optional unload hook container
 * @returns A new TelemetryContext instance
 */
export function createTelemetryContext(coreConfig, propertiesConfig, core, unloadHookContainer) {
    // Initialize context objects
    var app = new Application(propertiesConfig, core, unloadHookContainer);
    var cloud = new Cloud();
    var user = new User(coreConfig, propertiesConfig, core, unloadHookContainer);
    var os = new OperatingSystem(propertiesConfig, unloadHookContainer);
    var web = new Web(propertiesConfig, core, unloadHookContainer);
    var _sdk = new Sdk(coreConfig, core, unloadHookContainer);
    var _intWeb = new IntWeb(propertiesConfig, core, unloadHookContainer);
    var _utc = new Utc(propertiesConfig, unloadHookContainer);
    var loc = new Loc();
    var device = new Device();
    var _sessionManager = new SessionManager(core, propertiesConfig, unloadHookContainer);
    var session = new Session();
    var _overwriteEventPartA = !(propertiesConfig || {}).eventContainExtFields;
    function _getSessionId() {
        if (session && isString(session.customId)) {
            return session.customId;
        }
        _sessionManager.update();
        var autoSession = _sessionManager.automaticSession;
        if (autoSession) {
            var autoId = autoSession.getId();
            if (autoId && isString(autoId)) {
                session.automaticId = autoId;
            }
        }
        return session.automaticId;
    }
    var _parentId;
    var unloadHook = onConfigChange(propertiesConfig, function () {
        var _config = propertiesConfig;
        var traceCtx = _getTraceCtx(null, core);
        if (_config.enableDistributedTracing && !_parentId) {
            // When dt is enabled, both the traceId and spanId are required
            _parentId = generateW3CId().substring(0, 16);
        }
        if (traceCtx) {
            traceCtx.spanId = traceCtx.spanId || _parentId;
        }
    });
    unloadHookContainer && unloadHookContainer.add(unloadHook);
    // Return the telemetry context object
    var ctx = {
        app: null,
        user: null,
        os: null,
        web: null,
        session: null,
        device: null,
        cloud: null,
        loc: null,
        getTraceCtx: function (itemCtx) {
            return _getTraceCtx(itemCtx, core);
        },
        getSessionId: _getSessionId,
        applyApplicationContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 4 /* eExtensions.AppExt */, AppExtensionKeys, (_a = {},
                _a[0 /* eAppExtensionKeys.id */] = app.id,
                _a[1 /* eAppExtensionKeys.ver */] = app.ver,
                _a[2 /* eAppExtensionKeys.appName */] = app.name,
                _a[3 /* eAppExtensionKeys.locale */] = app.locale,
                _a[4 /* eAppExtensionKeys.expId */] = app.getExpId(),
                _a[5 /* eAppExtensionKeys.env */] = app.env,
                _a), _overwriteEventPartA);
        },
        applyUserContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 0 /* eExtensions.UserExt */, UserExtensionKeys, (_a = {},
                _a[1 /* eUserExtensionKeys.localId */] = user.getLocalId(),
                _a[0 /* eUserExtensionKeys.locale */] = user.locale,
                _a[2 /* eUserExtensionKeys.id */] = user.id,
                _a), _overwriteEventPartA);
        },
        applyWebContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 3 /* eExtensions.WebExt */, WebExtensionKeys, (_a = {},
                _a[0 /* eWebExtensionKeys.domain */] = web.domain,
                _a[1 /* eWebExtensionKeys.browser */] = web.browser,
                _a[2 /* eWebExtensionKeys.browserVer */] = web.browserVer,
                _a[3 /* eWebExtensionKeys.screenRes */] = web.screenRes,
                _a[5 /* eWebExtensionKeys.consentDetails */] = web.getUserConsentDetails(),
                _a[4 /* eWebExtensionKeys.userConsent */] = false,
                _a), _overwriteEventPartA);
        },
        applyOsContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 5 /* eExtensions.OSExt */, OSExtKeys, (_a = {},
                _a[0 /* eOSExtKeys.osName */] = os.name,
                _a[1 /* eOSExtKeys.ver */] = os.ver,
                _a), _overwriteEventPartA);
        },
        applySdkContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 6 /* eExtensions.SdkExt */, SDKExtKeys, (_a = {},
                _a[2 /* eSDKExtKeys.installId */] = _sdk.installId,
                _a[1 /* eSDKExtKeys.seq */] = _sdk.getSequenceId(),
                _a[3 /* eSDKExtKeys.epoch */] = _sdk.epoch,
                _a), _overwriteEventPartA);
        },
        applyIntWebContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 7 /* eExtensions.IntWebExt */, IntWebExtKeys, (_a = {},
                _a[0 /* eIntWebExtKeys.msfpc */] = _intWeb.getMsfpc(),
                _a[1 /* eIntWebExtKeys.anid */] = _intWeb.getAnid(),
                _a[2 /* eIntWebExtKeys.serviceName */] = _intWeb.serviceName,
                _a), _overwriteEventPartA);
        },
        applyUtcContext: function (event, itemCtx) {
            var _a;
            var utcFields = (_a = {},
                _a[0 /* eUtcExtKeys.popSample */] = _utc.popSample,
                _a);
            if (_utc.eventFlags > 0) {
                utcFields[1 /* eUtcExtKeys.eventFlags */] = _utc.eventFlags;
            }
            _applyExtValues(event, 8 /* eExtensions.UtcExt */, UtcExtKeys, utcFields, _overwriteEventPartA);
        },
        applyLocContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 9 /* eExtensions.LocExt */, LocExtKeys, (_a = {},
                _a[0 /* eLocExtKeys.tz */] = loc.tz,
                _a), _overwriteEventPartA);
        },
        applySessionContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 4 /* eExtensions.AppExt */, SessionExtKeys, (_a = {},
                _a[0 /* eSessionExtKeys.sessionId */] = _getSessionId(),
                _a), _overwriteEventPartA);
        },
        applyDeviceContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 1 /* eExtensions.DeviceExt */, DeviceExtKeys, (_a = {},
                _a[0 /* eDeviceExtKeys.localId */] = device.localId,
                _a[2 /* eDeviceExtKeys.make */] = device.make,
                _a[3 /* eDeviceExtKeys.model */] = device.model,
                _a[1 /* eDeviceExtKeys.deviceClass */] = device.deviceClass,
                _a), _overwriteEventPartA);
        },
        applyCloudContext: function (theEvent, itemCtx) {
            var _a;
            return _applyExtValues(theEvent, 10 /* eExtensions.CloudExt */, CloudExtKeys, (_a = {},
                _a[0 /* eCloudExtKeys.role */] = cloud.role,
                _a[1 /* eCloudExtKeys.roleInstance */] = cloud.roleInstance,
                _a[2 /* eCloudExtKeys.roleVer */] = cloud.roleVer,
                _a), _overwriteEventPartA);
        },
        applyAITraceContext: function (event, itemCtx) {
            var _a;
            if (propertiesConfig.enableApplicationInsightsTrace) {
                var distributedTrace = _getTraceCtx(itemCtx, core);
                if (distributedTrace) {
                    _applyExtValues(event, 2 /* eExtensions.TraceExt */, TraceExtKeys, (_a = {},
                        _a[0 /* eTraceExtKeys.traceId */] = distributedTrace.traceId,
                        _a[1 /* eTraceExtKeys.traceName */] = distributedTrace.pageName,
                        _a[2 /* eTraceExtKeys.parentId */] = distributedTrace.spanId || _parentId,
                        _a), false); // Always allow the event to define these values
                }
            }
        },
        applyDistributedTraceContext: function (event, itemCtx) {
            var _a;
            var distributedTrace = _getTraceCtx(itemCtx, core);
            if (distributedTrace && propertiesConfig.enableDistributedTracing) {
                var traceFields = (_a = {},
                    _a[0 /* eDistributedTraceExtKeys.traceId */] = distributedTrace.traceId,
                    _a[1 /* eDistributedTraceExtKeys.spanId */] = distributedTrace.spanId || _parentId,
                    _a);
                var traceFlags = distributedTrace.getTraceFlags();
                if (!isNullOrUndefined(traceFlags)) {
                    traceFields[2 /* eDistributedTraceExtKeys.traceFlags */] = traceFlags;
                }
                // Always allow the event to define these values
                _applyExtValues(event, 11 /* eExtensions.DtExt */, DistributedTraceExtKeys, traceFields, false);
                // TraceState and parent Id does not get added to ext.dt
                var parentCtx = distributedTrace.parentCtx;
                var traceState = distributedTrace.traceState;
                if (parentCtx || traceState) {
                    var partB = event.baseData = event.baseData || {};
                    if (parentCtx) {
                        var parentSpanId = parentCtx.spanId;
                        if (parentSpanId && parentSpanId !== traceFields[1 /* eDistributedTraceExtKeys.spanId */]) {
                            // The Span Id of the parent span is stored in Part B parentId
                            // https://msazure.visualstudio.com/One/_git/CommonSchema?path=/v4.0/PartB/traces/Span.md&version=GCc60e4d2f2c5c437f9588a48ffde0e6799afd5455&_a=preview&anchor=parentid
                            _applyValue(partB, "parentId", parentSpanId, false);
                        }
                    }
                    if (traceState && !traceState.isEmpty) {
                        // TraceState is a string representation of the trace state and should be sent in partB
                        // https://msazure.visualstudio.com/One/_git/CommonSchema?path=/v4.0/PartB/traces/Span.md&version=GCc60e4d2f2c5c437f9588a48ffde0e6799afd5455&anchor=tracestate&_a=preview
                        _applyValue(partB, "traceState", traceState.hdrs(1), false);
                    }
                }
            }
        }
    };
    return objDefineProps(ctx, {
        app: { g: function () { return app; }, s: function (value) { app = value; } },
        user: { g: function () { return user; }, s: function (value) { user = value; } },
        os: { g: function () { return os; }, s: function (value) { os = value; } },
        web: { g: function () { return web; }, s: function (value) { web = value; } },
        session: { g: function () { return session; }, s: function (value) { session = value; } },
        device: { g: function () { return device; }, s: function (value) { device = value; } },
        cloud: { g: function () { return cloud; }, s: function (value) { cloud = value; } },
        loc: { g: function () { return loc; }, s: function (value) { loc = value; } }
    });
}
//# sourceMappingURL=TelemetryContext.js.map